import axios from "axios";
import {
	decodeToken,
	getCurrentUser,
	ensureValidSession,
	refreshToken as refreshAuthToken,
	clearLocalAuth,
	isDefinitiveAuthFailure,
} from "./AuthService";

export { decodeToken, getCurrentUser };

const protocol = process.env.REACT_APP_API_PROTOCOL || "http://";

const useAxios = axios.create({
	baseURL: protocol + process.env.REACT_APP_API_URL + "api/",
	headers: {
		"Content-Type": "application/json",
		"Access-Control-Allow-Origin": "*",
	},
});

// 요청 시 만료 임박 토큰 선제 갱신 후 첨부
useAxios.interceptors.request.use(async (config) => {
	const isTokenEndpoint = config.url?.includes("users/token/");
	if (!isTokenEndpoint && localStorage.getItem("refresh")) {
		try {
			await ensureValidSession();
		} catch {
			// refresh 실패는 응답 인터셉터에서 처리
		}
	}

	const token = localStorage.getItem("access");
	if (token) {
		config.headers.Authorization = `Bearer ${token}`;
	}

	return config;
});

// 응답 인터셉터: 토큰 만료 처리
useAxios.interceptors.response.use(
	(response) => response, // 성공적인 응답 그대로 반환
	async (error) => {
		const originalRequest = error.config;

		// access 토큰이 만료된 경우
		if (
			error.response &&
			error.response.status === 401 &&
			!originalRequest._retry // 무한 루프 방지
		) {
			originalRequest._retry = true;

			// 토큰이 없는 경우 (로그인하지 않은 사용자)
			const storedRefreshToken = localStorage.getItem("refresh");
			if (!storedRefreshToken) {
				// 로그인하지 않은 사용자는 에러를 그대로 반환 (리디렉션하지 않음)
				return Promise.reject(error);
			}

			try {
				await refreshAuthToken();

				const newAccessToken = localStorage.getItem("access");
				originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

				return useAxios(originalRequest);
			} catch (refreshError) {
				console.error(
					"Token refresh failed:",
					refreshError,
				);

				if (isDefinitiveAuthFailure(refreshError)) {
					clearLocalAuth();

					const publicPaths = [
						"/login",
						"/logout",
						"/utils/rent-sanctuary/book-sanctuary",
						"/utils/rent-cars/share/",
						"/utils/rent-cars/apply",
					];
					const isPublicPath = publicPaths.some((path) =>
						window.location.pathname.includes(path),
					);

					if (
						!isPublicPath &&
						window.location.pathname !== "/login"
					) {
						window.dispatchEvent(
							new CustomEvent("tokenExpired"),
						);
					}
				}

				return Promise.reject(error);
			}
		}

		// 401이 아닌 다른 에러들 (400, 403, 404, 500 등)은 그대로 반환
		return Promise.reject(error);
	},
);

export default useAxios;
